import React, { useState } from 'react';
import clsx from 'clsx';
import { ChevronDownIcon } from '@heroicons/react/16/solid';
import { ListSelection } from './useListSelection';
import { RowCheckbox } from './RowCheckbox';

/**
 * Linear's grouped list (#603 Part 2 §2): a header per group with its icon
 * and count, rows beneath, and groups that collapse. Generic over the row,
 * so violations and work orders share one list.
 *
 * With `selection`, each group's header gets a checkbox that checks or
 * clears every row in it; the rows draw their own.
 */
export interface ListGroup<T> {
  key: string;
  label: string;
  icon: React.ReactNode;
  items: T[];
}

interface Props<T> {
  groups: ListGroup<T>[];
  /** One `<li>` per item. */
  renderRow: (item: T) => React.ReactNode;
  /** Prefix for the group headings' ids, unique per kind of list. */
  idPrefix: string;
  emptyMessage: string;
  selection?: ListSelection & { getKey: (item: T) => string };
}

export function GroupedList<T>({
  groups,
  renderRow,
  idPrefix,
  emptyMessage,
  selection,
}: Props<T>): React.ReactElement {
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({});

  if (groups.length === 0) {
    return (
      <p className="px-4 py-10 text-center text-[13px] text-fg-muted">
        {emptyMessage}
      </p>
    );
  }

  return (
    <div>
      {groups.map((g) => {
        const isCollapsed = !!collapsed[g.key];
        const headingId = `${idPrefix}-${g.key}`;
        return (
          <section key={g.key} aria-labelledby={headingId}>
            {/* The checkbox sits beside the heading, not in it, so the
                heading's name stays the group's. */}
            <div className="flex items-center bg-fg/[0.025]">
              {selection && (
                <GroupCheckbox
                  keys={g.items.map(selection.getKey)}
                  label={g.label}
                  selection={selection}
                />
              )}
              <h3 id={headingId} className="min-w-0 flex-1">
                <button
                  type="button"
                  onClick={() =>
                    setCollapsed((c) => ({ ...c, [g.key]: !isCollapsed }))
                  }
                  aria-expanded={!isCollapsed}
                  className={clsx(
                    'flex w-full items-center gap-2 py-1.5 pr-4 text-left text-[13px] font-medium text-fg-secondary hover:bg-fg/5',
                    selection ? 'pl-2' : 'pl-4'
                  )}
                >
                  <ChevronDownIcon
                    className={clsx(
                      'h-3.5 w-3.5 text-zinc-400 transition-transform',
                      isCollapsed && '-rotate-90'
                    )}
                    aria-hidden
                  />
                  {g.icon}
                  {g.label}
                  <span className="font-normal text-fg-muted">
                    {g.items.length}
                  </span>
                </button>
              </h3>
            </div>
            {!isCollapsed && (
              <ul className="divide-y divide-line/50">
                {g.items.map(renderRow)}
              </ul>
            )}
          </section>
        );
      })}
    </div>
  );
}

/** Checks every row in the group, or — when they all are — clears them. */
const GroupCheckbox: React.FC<{
  keys: string[];
  label: string;
  selection: ListSelection;
}> = ({ keys, label, selection }) => {
  const count = keys.filter((k) => selection.selected.has(k)).length;
  return (
    <span className="flex shrink-0 pl-4">
      <RowCheckbox
        checked={count > 0 && count === keys.length}
        indeterminate={count > 0 && count < keys.length}
        onChange={(on) => {
          const next = new Set(selection.selected);
          keys.forEach((k) => (on ? next.add(k) : next.delete(k)));
          selection.onChange(next);
        }}
        label={`Select all ${label}`}
      />
    </span>
  );
};

export default GroupedList;
